/**
 * The login screen (U-08-f; plan D1, D10; `docs/RBAC_AND_CONSOLE_FA.md` §۶).
 *
 * One form, and the discipline that matters in it: **each failure is rendered as the failure the
 * server actually named**, because the three refusals of `POST /ops/v1/sessions` have three
 * different next steps and merging them would send an operator looking for the wrong thing.
 *
 *  - 401 `signature-invalid` — one sentence for an unknown user AND a wrong password, and the
 *    wording here deliberately does not improve on it: §۶ refuses a message that would let the
 *    login screen tell an existing username from a missing one, and the façade runs the SAME key
 *    derivation on both paths — the same algorithm, the same iteration count, the same salt
 *    length — so the two failures cost the same derivation work and the answer's time does not
 *    separate them either.
 *  - 503 `auth-unconfigured` — not a credential problem at all: nobody seeded an owner, so there
 *    is nothing to type. It says that, and names the bootstrap variables that fix it. A 503 with
 *    any other problem code is *not* that answer — «no owner yet» would send the operator to the
 *    seed when the store is what moved — so it gets its own generic-unavailable sentence and the
 *    server's own `detail` line.
 *  - 429 `rate-limited` — the `(username, peer)` login budget (`ACS_LOGIN_RATE_PER_MINUTE`),
 *    with the server's own `Retry-After` wait rather than an invitation to retry immediately.
 *
 * The password is read off the submitted form and handed to the one function that accepts it. It
 * is never stored in state, never rendered, never part of an error sentence, and `session.ts`
 * keeps none of it.
 *
 * **R8 added a second step, not a second rule.** An account with an enrolled factor is answered
 * with `mfa_required` and a challenge instead of a token, so this screen swaps its form for a
 * six-digit field: no session exists while the factor is outstanding, the code is spent against
 * that one challenge, and a refusal keeps the operator on the step with the server's own sentence.
 * Both steps end in the same place — `session.ts` holds the token in module memory, which is what
 * `test/noBrowserCredentialStore.test.ts` pins for this file as much as for that one.
 */
import { useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ApiError, isApiError } from '../api/errors';
import { beginSignIn, submitSecondFactor, type SecondFactorChallenge } from '../api/session';
import { useSessionSnapshot } from '../lib/capability';
import { useConsoleApi } from '../app/ConsoleApiContext';
import { Icon } from '../components/Icon';
import { FIXTURE_CONSOLE_PASSWORD, FIXTURE_CONSOLE_USERS } from '../fixtures/session';

interface LoginFailure {
  /** Which of the three refusals this is, plus anything else the console can tell apart. */
  kind:
    | 'credentials'
    | 'unconfigured'
    | 'unavailable'
    | 'rate-limited'
    | 'second-factor'
    | 'transport'
    | 'refused'
    | 'unknown';
  detail: string;
  retryAfterSeconds?: number;
}

/** What the server said → what the operator reads. Both credential failures collapse into one. */
export function loginFailure(error: unknown): LoginFailure {
  if (isApiError(error)) {
    const api = error as ApiError;
    const detail = api.problem?.detail ?? api.message;
    if (api.kind === 'transport') return { kind: 'transport', detail };
    if (api.status === 429) {
      return {
        kind: 'rate-limited',
        detail,
        ...(api.retryAfterSeconds === undefined
          ? {}
          : { retryAfterSeconds: api.retryAfterSeconds }),
      };
    }
    if (api.problem?.code === 'auth-unconfigured') {
      return { kind: 'unconfigured', detail };
    }
    if (api.status === 503) {
      return { kind: 'unavailable', detail };
    }
    if (api.status === 401) return { kind: 'credentials', detail };
    return { kind: 'refused', detail };
  }
  return { kind: 'unknown', detail: error instanceof Error ? error.message : String(error) };
}

/**
 * The same mapping, for the second step.
 *
 * A 401 there cannot be a wrong password — the password was already accepted — so it must not
 * borrow the credentials sentence. Everything else (429 on the separate `totp:` budget, 503, a
 * dead transport) keeps exactly the meaning it has on the first step.
 */
export function secondFactorFailure(error: unknown): LoginFailure {
  const mapped = loginFailure(error);
  return mapped.kind === 'credentials' ? { ...mapped, kind: 'second-factor' } : mapped;
}

function FailurePanel({ failure }: { failure: LoginFailure }) {
  const copy: Record<LoginFailure['kind'], { title: string; note: string }> = {
    credentials: {
      title: 'نام کاربری یا گذرواژه با این استقرار نمی‌خواند',
      note: 'این جمله برای «کاربری وجود ندارد» و «گذرواژه غلط است» یکی است و عمدی است: §۶ اجازه نمی‌دهد صفحهٔ ورود بگوید کدام‌یک بوده.',
    },
    unconfigured: {
      title: 'این استقرار هنوز مالکی ندارد',
      note: 'هیچ کاربر فعالی seed نشده، پس هیچ گذرواژه‌ای برای حدس زدن وجود ندارد. گام بعدی سمت سرویس است: مقدار دادن ACS_OWNER_BOOTSTRAP_USERNAME و ACS_OWNER_BOOTSTRAP_PASSWORD و بالا آوردن دوبارهٔ façade.',
    },
    unavailable: {
      title: 'سرویس موقتاً در دسترس نیست',
      note: 'درِ نشست 503 داد ولی پاسخ auth-unconfigured نبود؛ این نه مشکل گذرواژه است نه «بی‌مالک» بودن استقرار. جملهٔ خودِ سرویس پایین آمده تا معلوم شود کدام نیمه نشسته است.',
    },
    'rate-limited': {
      title: 'تلاش‌های پشت سر هم بیش از سهمیهٔ ورود است',
      note: 'سهمیه به (نام کاربری، نشانی شما) بند است؛ تا پایان انتظار دوباره تلاش کنید.',
    },
    'second-factor': {
      title: 'کد دومرحله‌ای پذیرفته نشد',
      note: 'سرویس برای «کد غلط»، «challenge ناشناخته»، «challenge منقضی» و «challenge مصرف‌شده» یک جمله دارد و این صفحه هم آن‌ها را از هم جدا نمی‌کند. هر challenge تنها یک بار خرج می‌شود و چند کد غلط پشت سر هم آن را از کار می‌اندازد؛ اگر شک دارید از گام اول دوباره وارد شوید تا challenge تازه بگیرید.',
    },
    transport: {
      title: 'façade در دسترس نیست',
      note: 'هیچ پاسخی از سرویس نیامد؛ این خطای شبکه است، نه رد شدنِ گذرواژه.',
    },
    refused: {
      title: 'ورود پذیرفته نشد',
      note: 'سرویس پاسخ problem+json داد که به هیچ‌کدام از سه حالت شناخته‌شده نمی‌خورد.',
    },
    unknown: {
      title: 'ورود ممکن نشد',
      note: 'پاسخی که نیامد در قرارداد شناخته شده نیست.',
    },
  };
  const shown = copy[failure.kind];
  return (
    <div
      className="rounded-panel border border-status-failed-line bg-status-failed-soft p-panel"
      role="alert"
      data-testid="login-failure"
      data-failure={failure.kind}
    >
      <div className="flex items-start gap-2">
        <Icon name="warning" className="mt-0.5 shrink-0 text-status-failed-ink" />
        <div className="min-w-0">
          <p className="text-ui font-semibold text-ink">{shown.title}</p>
          <p className="mt-1 text-caption text-ink-dim">{shown.note}</p>
          {failure.kind === 'rate-limited' ? (
            <p className="mt-2 text-body text-ink" data-testid="login-retry-after">
              {failure.retryAfterSeconds === undefined
                ? 'کمی صبر کنید و دوباره تلاش کنید.'
                : `حدود ${failure.retryAfterSeconds} ثانیه دیگر دوباره تلاش کنید.`}
            </p>
          ) : null}
          {/* The server's own sentence, kept visible for an operator opening a ticket. */}
          <p
            className="mono mt-2 break-words text-caption text-ink-faint"
            data-testid="login-detail"
          >
            {failure.detail}
          </p>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const api = useConsoleApi();
  const location = useLocation();
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<LoginFailure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Set only while the password step has a second factor outstanding — R8's challenge. */
  const [challenge, setChallenge] = useState<SecondFactorChallenge | null>(null);
  /**
   * The username the password step authenticated. A username is not a secret (the field keeps it
   * anyway, and the badge reads the server's copy once the session exists); no password is kept.
   */
  const [rememberedUsername, setRememberedUsername] = useState('');

  const snapshot = useSessionSnapshot();
  if (snapshot.status === 'signed-in') {
    // Reaching the login screen while a session is live means the gate let you through twice
    // (a second tab, a stale render). There is nothing to sign in to.
    return <Navigate to={redirectTo(location)} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFailure(null);
    setNotice(null);
    const username = usernameRef.current?.value ?? '';
    const password = passwordRef.current?.value ?? '';
    setPending(true);
    try {
      const step = await beginSignIn(username, password);
      if (passwordRef.current) passwordRef.current.value = '';
      if (step.step === 'second-factor') {
        // R8: the password was accepted and NO session exists yet. Nothing is stored — the
        // challenge is only the ticket to the next step, and `submitSecondFactor` is what mints.
        setRememberedUsername(username);
        setChallenge(step.challenge);
        return;
      }
      // On success this component unmounts behind the gate; nothing here may keep the password.
      if (passwordRef.current) passwordRef.current.value = '';
    } catch (error) {
      setFailure(loginFailure(error));
      if (passwordRef.current) passwordRef.current.value = '';
    } finally {
      setPending(false);
    }
  }

  async function onSubmitCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (challenge === null) return;
    setFailure(null);
    setNotice(null);
    const code = (codeRef.current?.value ?? '').replace(/\s+/g, '');
    setPending(true);
    try {
      await submitSecondFactor(challenge.challenge, code);
      // Success unmounts this screen behind the gate, exactly as the one-step login did.
      if (codeRef.current) codeRef.current.value = '';
    } catch (error) {
      // The challenge is not dropped here: the server keeps it until the code is accepted, five
      // wrong attempts are spent on it, or it expires — and the panel's note says what that means.
      setFailure(secondFactorFailure(error));
      if (codeRef.current) codeRef.current.value = '';
    } finally {
      setPending(false);
    }
  }

  /** Back to the username step: the outstanding challenge is abandoned, nothing else is kept. */
  function backToCredentials() {
    setChallenge(null);
    setFailure(null);
    setNotice(null);
  }

  const failurePanel = failure ? <FailurePanel failure={failure} /> : null;

  const ended = snapshot.signedOutReason === 'unauthorized';

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-void px-4 py-8">
      <div className="w-full max-w-md">
        <div className="mb-4 flex items-center gap-2 text-ink-dim">
          <Icon name="shield" size={16} />
          <span className="text-label">
            {api.dataSource === 'http'
              ? 'کنسول عملیاتی ACS — ورود'
              : 'کنسول عملیاتی ACS — ورود (حالت نمونه)'}
          </span>
        </div>

        {challenge === null ? (
          <form
            className="panel p-panel"
            onSubmit={(event) => void onSubmit(event)}
            data-testid="login-form"
          >
            <h1 className="text-title font-semibold text-ink">ورود به کنسول</h1>
            <p className="mt-1 text-body text-ink-dim">
              یک نشست انسانی با `POST /ops/v1/sessions` باز می‌شود. توکن فقط در حافظهٔ همین صفحه
              می‌ماند؛ با بستن تب یا تازه‌کردن صفحه باید دوباره وارد شوید.
            </p>

            {ended ? (
              <p className="chip chip-stale mt-3" data-testid="login-notice">
                نشست شما باطل شد؛ دوباره وارد شوید.
              </p>
            ) : null}
            {notice ? <p className="mt-3 text-body text-ink-dim">{notice}</p> : null}
            {failure ? <div className="mt-3">{failurePanel}</div> : null}

            <label className="field mt-4">
              <span className="field-label">نام کاربری</span>
              <input
                ref={usernameRef}
                className="input"
                name="username"
                autoComplete="username"
                defaultValue={rememberedUsername}
                autoFocus
                dir="ltr"
                data-testid="login-username"
              />
            </label>

            <label className="field mt-3">
              <span className="field-label">گذرواژه</span>
              <input
                ref={passwordRef}
                className="input"
                type="password"
                name="password"
                autoComplete="current-password"
                dir="ltr"
                data-testid="login-password"
              />
            </label>

            <button
              type="submit"
              className="btn btn-primary mt-4 w-full justify-center"
              disabled={pending}
              data-testid="login-submit"
            >
              {pending ? 'در حال بررسی…' : 'ورود'}
            </button>

            {api.dataSource === 'fixture' ? (
              <p className="mt-3 text-caption text-ink-faint" data-testid="login-demo-hint">
                حالت نمونه به سرویزی وصل نمی‌شود؛ همین حساب‌های نمونه را می‌شناسد:{' '}
                <span className="mono" dir="ltr">
                  {FIXTURE_CONSOLE_USERS.map((user) => user.username).join('، ')}
                </span>{' '}
                با گذرواژهٔ{' '}
                <span className="mono" dir="ltr">
                  {FIXTURE_CONSOLE_PASSWORD}
                </span>
                .
              </p>
            ) : null}
          </form>
        ) : (
          <form
            className="panel p-panel"
            onSubmit={(event) => void onSubmitCode(event)}
            data-testid="login-totp-form"
          >
            <h1 className="text-title font-semibold text-ink">گام دوم: کد تأیید</h1>
            <p className="mt-1 text-body text-ink-dim">
              گذرواژهٔ{' '}
              <span className="mono" dir="ltr">
                {rememberedUsername || '—'}
              </span>{' '}
              پذیرفته شد، ولی این حساب عامل دوم دارد؛ نشستی باز نشده و هنوز توکنی وجود ندارد. کد
              شش‌رقمی برنامهٔ احراز هویت را وارد کنید.
            </p>

            {failure ? <div className="mt-3">{failurePanel}</div> : null}

            <label className="field mt-4">
              <span className="field-label">کد شش‌رقمی</span>
              <input
                ref={codeRef}
                className="input mono"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                dir="ltr"
                autoFocus
                data-testid="login-totp-code"
              />
            </label>

            <button
              type="submit"
              className="btn btn-primary mt-4 w-full justify-center"
              disabled={pending}
              data-testid="login-totp-submit"
            >
              {pending ? 'در حال بررسی…' : 'تأیید کد'}
            </button>

            <button
              type="button"
              className="btn btn-ghost mt-2 w-full justify-center"
              onClick={backToCredentials}
              disabled={pending}
              data-testid="login-totp-back"
            >
              بازگشت به نام کاربری و گذرواژه
            </button>

            <p className="mt-3 text-caption text-ink-faint" data-testid="login-totp-expiry">
              این گام موقتی است: هر challenge تنها یک بار خرج می‌شود و چند کد غلط پشت سر هم آن را از
              کار می‌اندازد.
              {challenge.expiresAt === null ? null : (
                <>
                  {' '}
                  اعتبار اعلام‌شده تا{' '}
                  <span className="mono" dir="ltr">
                    {challenge.expiresAt}
                  </span>{' '}
                  است.
                </>
              )}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

/** Where a finished login goes: the page the gate bounced the operator off, or the dashboard. */
function redirectTo(location: ReturnType<typeof useLocation>): string {
  const from = (location.state as { from?: string } | null)?.from;
  return typeof from === 'string' && from.length > 0 ? from : '/';
}
